import { type FieldFunctionOptions } from "@apollo/client"
import { type KeyArgsFunction } from "@apollo/client/cache/inmemory/policies"
import {
  type AuditLogEvent,
  type AuditLogPage,
  type QueryReadAuditLogArgs,
  type QueryReadAuditLogEventArgs,
} from "@digits-graphql/frontend/graphql-bearer"
import { allKeyArgs } from "src/shared/initializers/typePolicies/helpers/allKeyArgs"

// The page anchor is not part of the cache key: the first page answers the same with or without
// it, so keying on it would refetch the page it just received.
const PAGE_EXCLUDED_KEYS = ["page", "pageAnchor"]

const pageKeyArgs: KeyArgsFunction = (args, context) =>
  args ? allKeyArgs(args, PAGE_EXCLUDED_KEYS) : context.fieldName

// An AuditLogPage is an object, not a list, so the shared offsetLimitPagination cannot merge it.
// A later page appends its events. The total, the kinds, and the actor options come from the
// first page only, because the service fills them on the first page only.
export function auditLogPagePagination() {
  return {
    keyArgs: pageKeyArgs,
    merge(
      existing: AuditLogPage | undefined,
      incoming: AuditLogPage,
      { args }: FieldFunctionOptions<QueryReadAuditLogArgs>
    ): AuditLogPage {
      if (!existing || (args?.page.offset ?? 0) === 0) {
        return incoming
      }
      return {
        ...existing,
        events: [...existing.events, ...incoming.events],
        pageAnchor: incoming.pageAnchor,
      }
    },
  }
}

// The event query pages its references. A later page appends them onto the event held.
export function auditLogEventReferencesPagination() {
  return {
    keyArgs: ["legalEntityId", "id"],
    merge(
      existing: AuditLogEvent | null | undefined,
      incoming: AuditLogEvent | null,
      { args }: FieldFunctionOptions<QueryReadAuditLogEventArgs>
    ): AuditLogEvent | null {
      if (!existing || !incoming || (args?.referencesPage?.offset ?? 0) === 0) {
        return incoming
      }
      return {
        ...incoming,
        references: [...existing.references, ...incoming.references],
      }
    },
  }
}
